//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useDebounce } from 'use-debounce';

import {
  IContentLibrarySourcesOptions,
  MediaFilterParamKey,
} from '@apis/content-library/contentLibraryApiModel';
import HR from '@components/atoms/hr/HR';
import { CollapsibleSection } from '@components/media-filters/CollapsibleSection';
import {
  FilterSection,
  FilterSectionTitle,
  FilterValue,
} from '@components/media-filters/filterSectionModels';
import { getSection } from '@components/media-filters/mediaFiltersUtils';
import { useContentLibraryParamsMapper } from '@components/media-filters/useContentLibraryParamsMapper';
import { useMediaLibraryLabelsInfiniteQuery } from '@components/media-filters/useMediaLibraryLabelsInfiniteQuery';
import { LabelsCheckbox } from '@content-drawer/filters/LabelsCheckbox';
import { SelectLabelsSection } from '@content-drawer/filters/SelectLabelsSection';
import { ManageLabelsDialog } from '@content-drawer/filters/labels/ManageLabelsDialog';
import { getIsLabelManagementEnabled } from '@content-drawer/redux/getContentDrawer';

export const CONTENT_DRAWER_LABELS_LIMIT = 5;

export const DEFAULT_LABELS_SECTION: FilterSection<string> = {
  options: [],
  paramKey: 'labels',
};

export interface LabelsFilterProps<T extends FilterValue> {
  isDisabled?: boolean;
  isInitiallyCollapsed?: boolean;
  onChange: (
    sections: Map<MediaFilterParamKey, FilterSection<FilterValue>>,
  ) => void;
  sections: Map<MediaFilterParamKey, FilterSection<T>>;
  section: FilterSection<string> | undefined;
  sources: IContentLibrarySourcesOptions;
}

export const LabelsFilter = <T extends FilterValue>({
  isDisabled,
  isInitiallyCollapsed,
  onChange,
  section = DEFAULT_LABELS_SECTION,
  sections,
  sources,
}: LabelsFilterProps<T>) => {
  const isLabelManagementEnabled = useSelector(getIsLabelManagementEnabled);

  const { filters } = useContentLibraryParamsMapper({
    sections,
    skipLabels: true,
  });
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText] = useDebounce(searchText, 300);
  const { data, error, isError, isFetching } =
    useMediaLibraryLabelsInfiniteQuery({
      query: {
        autocompleteType: 'Labels',
        filters,
        limit: CONTENT_DRAWER_LABELS_LIMIT,
        // Search using debounced text. However, when the user removes the entire search term, use an empty string to fetch data from the cache.
        prefix: searchText === '' ? searchText : debouncedSearchText,
        sources,
      },
    });

  return (
    <>
      <CollapsibleSection
        isCollapsed={isInitiallyCollapsed}
        rightComponent={isLabelManagementEnabled && <ManageLabelsDialog />}
        title={FilterSectionTitle.labels}
      >
        <LabelsCheckbox
          isDisabled={isDisabled}
          onChange={onChange}
          section={getSection(sections, 'hasLabels')}
          subsection={getSection(sections, 'labels')}
        />
        <SelectLabelsSection
          error={error}
          isDisabled={isDisabled}
          isError={isError}
          isFetching={isFetching}
          labels={data?.pages.flatMap((page) => page) ?? []}
          onChange={onChange}
          searchText={searchText}
          section={section}
          sections={sections}
          setSearchText={setSearchText}
        />
      </CollapsibleSection>
      <HR />
    </>
  );
};
